// Magic-link / OTP sign-in. Two-step flow:
// 1. User enters email → POST /auth/start → pending_id
// 2. User enters 6-digit code → POST /auth/verify → AuthResponse (token + identity + user)
// On success, session is written to localStorage and the user is navigated home.
//
// Hardening notes:
//   - inFlightRef guards both submits against double-fire from rapid double-clicks
//     or React state propagation races that leave the `disabled` prop stale.
//   - OTP input auto-submits on the 6th digit (typed or pasted), matching the
//     South Seas sign-in pattern — the Verify button exists as a fallback.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeSwitcher } from "../theme/ThemeSwitcher";
import { BrandMark } from "../layout/Brand";
import { ApiError, authStart, authVerifyOtp, saveSession } from "../api/client";
import { getRecentEmails, rememberEmail } from "./recentEmails";

type Stage = "email" | "otp";

export function SignInPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>("email");
  const [email, setEmail] = useState("");
  const [pendingId, setPendingId] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [recents, setRecents] = useState<string[]>([]);
  const inFlightRef = useRef(false);

  // Load the cookie-backed recent-email list on mount. Pre-fill the
  // input with the most-recent so a returning Captain can hit Send
  // code without typing — and the rest populate a <datalist> for
  // browser-native dropdown suggestions.
  useEffect(() => {
    const list = getRecentEmails();
    setRecents(list);
    if (list[0] && !email) setEmail(list[0]);
    // Intentional: only run on mount; subsequent recents updates
    // come from rememberEmail in doStart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function doStart() {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setError("");
    setBusy(true);
    try {
      const trimmed = email.trim().toLowerCase();
      const { pending_id } = await authStart(trimmed);
      // Only remember after the backend accepts the email — avoids
      // populating the dropdown with typos.
      rememberEmail(trimmed);
      setRecents(getRecentEmails());
      setPendingId(pending_id);
      setStage("otp");
      setCode("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not start sign-in");
    } finally {
      setBusy(false);
      inFlightRef.current = false;
    }
  }

  async function doVerify(value: string) {
    if (inFlightRef.current) return;
    if (value.length !== 6) return;
    inFlightRef.current = true;
    setError("");
    setBusy(true);
    try {
      const resp = await authVerifyOtp(pendingId, value);
      saveSession(resp);
      navigate("/home");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Invalid or expired code");
      setCode("");
    } finally {
      setBusy(false);
      inFlightRef.current = false;
    }
  }

  function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    void doStart();
  }

  function submitOtp(e: React.FormEvent) {
    e.preventDefault();
    void doVerify(code);
  }

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center bg-surface px-6 text-primary transition-colors">
      <div className="absolute right-4 top-4">
        <ThemeSwitcher />
      </div>

      <div className="mb-12">
        <BrandMark variant="hero" />
      </div>

      <div className="w-full max-w-sm rounded-panel border border-edge bg-surface-glass p-8 shadow-[var(--wt-shadow)] backdrop-blur">
        {stage === "email" ? (
          <form onSubmit={submitEmail} className="flex flex-col gap-4">
            <p className="text-center text-sm text-muted">Sign in to Waytide</p>
            {/* Two layers of recall here:
                  1. `name="email"` + `autoComplete="email"` activates
                     the browser's profile-level autofill (the one
                     surfaced by Chrome / 1Password / Safari etc.).
                  2. `list="signin-recent-emails"` + a <datalist>
                     populated from a cookie-backed memory of past
                     sign-ins shows a native dropdown of the
                     last few emails this device has used — handy
                     when the browser-profile autofill is empty (new
                     browser, incognito persisted, etc.) but the
                     user has signed in here before. Cookie persists
                     1 year and survives most cache wipes. */}
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus
              autoComplete="email"
              list="signin-recent-emails"
              className="rounded-md border border-edge bg-surface-raised px-4 py-3 text-center text-lg text-primary outline-none transition-colors placeholder:text-faint focus:border-accent"
              data-testid="signin-email"
            />
            {recents.length > 0 && (
              <datalist id="signin-recent-emails" data-testid="signin-recent-emails">
                {recents.map((e) => (
                  <option key={e} value={e} />
                ))}
              </datalist>
            )}
            <button
              type="submit"
              disabled={busy || !email.includes("@")}
              className="cursor-pointer rounded-md bg-accent px-4 py-3 text-sm font-semibold text-[var(--wt-selection-text)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="signin-submit-email"
            >
              {busy ? "Sending…" : "Send code"}
            </button>
          </form>
        ) : (
          <form onSubmit={submitOtp} className="flex flex-col gap-4">
            <p className="text-center text-sm text-muted">
              Enter the 6-digit code sent to
            </p>
            <p className="text-center text-sm font-semibold text-primary">{email}</p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                setCode(v);
                if (v.length === 6) {
                  void doVerify(v);
                }
              }}
              placeholder="000000"
              required
              autoFocus
              // tracking-[0.5em] is typography for digit chunking,
              // not status-label wide-tracking. Coral-blessed site.
              className="rounded-md border border-edge bg-surface-raised px-4 py-3 text-center text-2xl font-bold tracking-[0.5em] text-primary outline-none transition-colors placeholder:text-faint focus:border-accent"
              data-testid="signin-otp"
            />
            {busy && (
              <p className="text-center text-xs text-muted">
                Verifying…
              </p>
            )}
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className="cursor-pointer rounded-md bg-accent px-4 py-3 text-sm font-semibold text-[var(--wt-selection-text)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="signin-submit-otp"
            >
              Verify
            </button>
            <button
              type="button"
              onClick={() => {
                setStage("email");
                setCode("");
                setError("");
              }}
              className="cursor-pointer text-xs text-muted transition-colors hover:text-primary"
            >
              Use a different email
            </button>
          </form>
        )}

        {error && (
          <p className="mt-4 text-center text-xs text-danger" data-testid="signin-error">
            {error}
          </p>
        )}
      </div>

      <p className="mt-6 text-xs text-faint">
        Pave your way through a changing tide.
      </p>
    </div>
  );
}
